import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const KEY = 'edgeai-theme';

/** 讀使用者選擇。讀不到（無痕模式、封鎖 storage）就當作跟隨系統，不要拋錯。 */
export function storedTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function prefersDark(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

/** 把主題套到 <html>。index.html 的行內腳本也呼叫同一套規則，避免載入時閃一下白底。 */
export function applyTheme(theme: Theme) {
  const dark = theme === 'dark' || (theme === 'system' && prefersDark());
  document.documentElement.classList.toggle('dark', dark);
  // 讓瀏覽器原生元件（捲軸、日期選擇器）跟著換色，否則深色頁面上會出現白色日曆
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(storedTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      if (theme === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, theme);
    } catch { /* storage 不可用時僅本次有效，不影響畫面 */ }
  }, [theme]);

  // 跟隨系統時，使用者在作業系統切換就要即時反應
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  /** 在「淺色 → 深色 → 跟隨系統」之間輪替 */
  const cycle = useCallback(() => {
    setThemeState((t) => (t === 'light' ? 'dark' : t === 'dark' ? 'system' : 'light'));
  }, []);

  return { theme, setTheme: setThemeState, cycle };
}
